import React, { useState, useEffect } from 'react';
import {
  Box, TextField, Button, Typography, IconButton, CircularProgress,
  InputAdornment, Avatar, Paper, Divider, Grid, Card, CardContent,
  Switch, FormControlLabel, Chip, Stack, Alert
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import LinkIcon from '@mui/icons-material/Link';
import ImageIcon from '@mui/icons-material/Image';
import ClearIcon from '@mui/icons-material/Clear';
import TitleIcon from '@mui/icons-material/Title';
import TranslateIcon from '@mui/icons-material/Translate';
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';
import FormatQuoteIcon from '@mui/icons-material/FormatQuote';
import { validateImageFile } from './utils/imageUtils';
import { getEditableContent, isHtml, enhancePlainText } from './utils/contentUtils';
import api from './api';
import EnhancedRichTextEditor from './components/EnhancedRichTextEditor';

const EnhancedHandbookForm = ({ 
  initialData = null, 
  onSubmit, 
  onCancel, 
  isEdit = false 
}) => {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    onlineUrl: '',
    coverImage: '',
    imageUploading: false
  });
  const [imagePreview, setImagePreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [errors, setErrors] = useState({});
  const [previewMode, setPreviewMode] = useState(false);
  const [showTranslation, setShowTranslation] = useState(true);
  const [canEnhance, setCanEnhance] = useState(false);

  useEffect(() => {
    if (initialData) {
      // Convert description to HTML format if it's plain text
      const editableDescription = getEditableContent(initialData.description || '');
      const originalIsHtml = isHtml(initialData.description || '');
      
      console.log('📝 Loading handbook data:', {
        title: initialData.title,
        originalDescription: initialData.description,
        convertedDescription: editableDescription,
        isOriginalHtml: originalIsHtml
      });
      
      // Check if content can be enhanced (plain text that could benefit from rich formatting)
      setCanEnhance(!originalIsHtml && initialData.description && initialData.description.trim().length > 0);
      
      setFormData({
        title: initialData.title || '',
        description: editableDescription,
        onlineUrl: initialData.onlineUrl || '',
        coverImage: initialData.coverImage || '',
        imageUploading: false
      });
      if (initialData.coverImage) {
        setImagePreview(initialData.coverImage);
      }
    }
  }, [initialData]);

  const handleChange = (field) => (event) => {
    setFormData({
      ...formData,
      [field]: event.target.value
    });
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors({ ...errors, [field]: '' });
    }
  };

  const handleDescriptionChange = (content) => {
    setFormData({
      ...formData,
      description: content
    });
    if (errors.description) {
      setErrors({ ...errors, description: '' });
    }
  };

  const handleEnhanceContent = () => {
    if (formData.description) {
      const enhancedContent = enhancePlainText(formData.description);
      console.log('🎨 Enhancing content:', {
        original: formData.description,
        enhanced: enhancedContent
      });
      setFormData({
        ...formData,
        description: enhancedContent
      });
      setCanEnhance(false);
    }
  };

  const handleImageSelect = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setFormData(prev => ({
      ...prev,
      imageUploading: true
    }));

    try {
      validateImageFile(file);
      const processedFile = file;
      
      const formDataObj = new FormData();
      formDataObj.append("image", processedFile);

      const response = await api.post('/upload-image-raw', formDataObj, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      if (response.data && response.data.success && response.data.url) {
        setFormData(prev => ({
          ...prev,
          coverImage: response.data.url
        }));
        setImagePreview(response.data.url);
        setErrors({ ...errors, image: '' });
      } else {
        throw new Error('Failed to upload image');
      }
    } catch (error) {
      console.error("Error uploading image:", error);
      setErrors({ ...errors, image: error.message || "Failed to upload image. Please try again." });
    } finally {
      setFormData(prev => ({
        ...prev,
        imageUploading: false
      }));
    }
  };

  const handleRemoveImage = () => {
    setImagePreview(null);
    setFormData({ ...formData, coverImage: '' });
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.title.trim()) {
      newErrors.title = 'Title is required';
    }
    
    if (!formData.description.trim() || formData.description === '<p></p>') {
      newErrors.description = 'Description is required';
    }
    
    if (formData.onlineUrl && !isValidUrl(formData.onlineUrl)) {
      newErrors.onlineUrl = 'Please enter a valid URL';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const isValidUrl = (string) => {
    try {
      new URL(string);
      return true;
    } catch (_) {
      return false;
    }
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setUploading(true);
    try {
      await onSubmit(formData);
    } catch (error) {
      console.error('Error submitting handbook:', error);
      setErrors({ ...errors, submit: error.message || 'Failed to submit handbook' });
    } finally {
      setUploading(false);
    }
  };

  const stripHtml = (html) => {
    const tmp = document.createElement("DIV");
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || "";
  };

  return (
    <Box sx={{ maxWidth: 900, mx: 'auto', p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <TitleIcon color="primary" fontSize="large" />
          {isEdit ? 'Edit Handbook' : 'Create New Handbook'}
        </Typography>
        <Typography variant="body1" color="text.secondary">
          {isEdit ? 'Update your handbook information and content' : 'Create a comprehensive handbook with rich content'}
        </Typography>
      </Box>

      <Grid container spacing={3}>
        {/* Left Column - Form Fields */}
        <Grid item xs={12} md={8}>
          <Stack spacing={3}>
            {/* Basic Information Card */}
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <TitleIcon color="primary" />
                  Basic Information
                </Typography>
                <Divider sx={{ mb: 3 }} />
                
                <TextField
                  fullWidth
                  label="Handbook Title"
                  value={formData.title}
                  onChange={handleChange('title')}
                  error={!!errors.title}
                  helperText={errors.title || 'Enter a clear, descriptive title for your handbook'}
                  margin="normal"
                  required
                  variant="outlined"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <TitleIcon color="action" />
                      </InputAdornment>
                    ),
                  }}
                />

                <TextField
                  fullWidth
                  label="Online URL (Optional)"
                  value={formData.onlineUrl}
                  onChange={handleChange('onlineUrl')}
                  error={!!errors.onlineUrl}
                  helperText={errors.onlineUrl || 'Link to online version of the handbook (leave empty if not available)'}
                  margin="normal"
                  variant="outlined"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LinkIcon color="action" />
                      </InputAdornment>
                    ),
                  }}
                />
              </CardContent>
            </Card>

            {/* Rich Text Description */}
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <FormatBoldIcon color="primary" />
                    Detailed Description
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={previewMode}
                          onChange={(e) => setPreviewMode(e.target.checked)}
                          size="small"
                        />
                      }
                      label="Preview"
                    />
                  </Box>
                </Box>
                
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Provide a comprehensive description of the handbook content. Use the formatting tools to enhance readability.
                </Typography>

                {errors.description && (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    {errors.description}
                  </Alert>
                )}

                {canEnhance && (
                  <Alert 
                    severity="info" 
                    sx={{ mb: 2 }}
                    action={
                      <Button 
                        color="inherit" 
                        size="small" 
                        onClick={handleEnhanceContent}
                        startIcon={<FormatBoldIcon />}
                      >
                        Enhance
                      </Button>
                    }
                  >
                    <Typography variant="subtitle2" gutterBottom>
                      Legacy Content Detected
                    </Typography>
                    This handbook contains plain text content. Click "Enhance" to convert it to rich format with improved formatting detection.
                  </Alert>
                )}

                {previewMode ? (
                  <Paper 
                    variant="outlined" 
                    sx={{ 
                      p: 3, 
                      minHeight: 300,
                      bgcolor: 'grey.50',
                      '& img': {
                        maxWidth: '100%',
                        height: 'auto',
                        borderRadius: '4px',
                        margin: '0.5em 0'
                      },
                      /* Simple Emoji Scaling - Follows Text Size */
                      '& .emoji-inline, & img[data-emoji-name], & img[alt*="emoji"]': {
                        height: '1em !important',
                        width: 'auto !important',
                        display: 'inline !important',
                        verticalAlign: 'middle !important',
                        margin: '0 0.1em !important',
                        borderRadius: '0 !important',
                        objectFit: 'contain !important',
                        border: 'none !important',
                        background: 'transparent !important',
                        fontSize: 'inherit !important',
                        lineHeight: '1 !important',
                      },
                      '& p': {
                        margin: '0.8em 0',
                        lineHeight: 1.6
                      },
                      '& h1': {
                        fontSize: '2rem',
                        color: '#1976d2',
                        fontWeight: 700,
                        margin: '1em 0 0.5em 0',
                      },
                      '& h2': {
                        fontSize: '1.5rem',
                        color: '#1976d2',
                        fontWeight: 600,
                        margin: '1em 0 0.5em 0',
                      },
                      '& h3': {
                        fontSize: '1.25rem',
                        color: '#1976d2',
                        fontWeight: 600,
                        margin: '1em 0 0.5em 0',
                      },
                      '& ul, & ol': {
                        paddingLeft: '1.5em',
                        margin: '1em 0'
                      },
                      '& blockquote': {
                        borderLeft: '4px solid #1976d2',
                        marginLeft: 0,
                        paddingLeft: '1.2em',
                        fontStyle: 'italic',
                        backgroundColor: 'rgba(25, 118, 210, 0.04)',
                        padding: '0.8em 1.2em',
                        margin: '1.5em 0',
                        borderRadius: '0 4px 4px 0'
                      }
                    }}
                  >
                    <Typography variant="subtitle2" color="primary" gutterBottom>
                      Preview Mode
                    </Typography>
                    <div dangerouslySetInnerHTML={{ __html: formData.description }} />
                  </Paper>
                ) : (
                  <EnhancedRichTextEditor
                    content={formData.description}
                    onChange={handleDescriptionChange}
                    placeholder="Describe your handbook content in detail. Include topics covered, target audience, key takeaways, and any special features..."
                  />
                )}

                <Box sx={{ mt: 2 }}>
                  <Alert severity="info" icon={<TranslateIcon />}>
                    <Typography variant="subtitle2" gutterBottom>
                      Translation Support
                    </Typography>
                    After saving, this handbook will be automatically translated to multiple languages for international users.
                    <Box sx={{ mt: 1 }}>
                      <Chip label="English" size="small" sx={{ mr: 0.5 }} />
                      <Chip label="German" size="small" sx={{ mr: 0.5 }} />
                      <Chip label="Spanish" size="small" sx={{ mr: 0.5 }} />
                      <Chip label="French" size="small" sx={{ mr: 0.5 }} />
                      <Chip label="Italian" size="small" sx={{ mr: 0.5 }} />
                      <Chip label="+8 more" size="small" />
                    </Box>
                  </Alert>
                </Box>
              </CardContent>
            </Card>
          </Stack>
        </Grid>

        {/* Right Column - Cover Image */}
        <Grid item xs={12} md={4}>
          <Card sx={{ position: 'sticky', top: 20 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <ImageIcon color="primary" />
                Cover Image
              </Typography>
              <Divider sx={{ mb: 3 }} />
              
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Upload a high-quality cover image that represents your handbook content.
              </Typography>

              {imagePreview ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                  <Box sx={{ position: 'relative', display: 'inline-block' }}>
                    <Avatar
                      src={imagePreview}
                      variant="rounded"
                      sx={{ 
                        width: '100%', 
                        height: 200,
                        boxShadow: 3,
                        border: '2px solid',
                        borderColor: 'divider'
                      }}
                    />
                    <IconButton
                      size="small"
                      sx={{
                        position: 'absolute',
                        top: -8,
                        right: -8,
                        bgcolor: 'error.main',
                        color: 'white',
                        boxShadow: 2,
                        '&:hover': {
                          bgcolor: 'error.dark',
                        }
                      }}
                      onClick={handleRemoveImage}
                    >
                      <ClearIcon />
                    </IconButton>
                  </Box>
                  <Typography variant="body2" color="text.secondary" align="center">
                    Cover image uploaded successfully
                  </Typography>
                </Box>
              ) : (
                <Box sx={{ 
                  border: '2px dashed', 
                  borderColor: 'divider', 
                  borderRadius: 2, 
                  p: 3, 
                  textAlign: 'center',
                  backgroundColor: 'grey.50'
                }}>
                  <ImageIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                  <Typography variant="subtitle1" color="text.secondary" gutterBottom>
                    No cover image selected
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Choose a high-quality image
                  </Typography>
                  <Button
                    variant="contained"
                    component="label"
                    startIcon={formData.imageUploading ? <CircularProgress size={20} /> : <CloudUploadIcon />}
                    disabled={formData.imageUploading}
                    fullWidth
                  >
                    {formData.imageUploading ? 'Uploading...' : 'Select Image'}
                    <input
                      type="file"
                      hidden
                      accept="image/*"
                      onChange={handleImageSelect}
                      disabled={formData.imageUploading}
                    />
                  </Button>
                </Box>
              )}
              
              {errors.image && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  {errors.image}
                </Alert>
              )}

              {/* Character count */}
              <Box sx={{ mt: 3, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  <strong>Content Statistics:</strong>
                </Typography>
                <Typography variant="body2" sx={{ mt: 0.5 }}>
                  Characters: {stripHtml(formData.description).length}
                </Typography>
                <Typography variant="body2">
                  Words: {stripHtml(formData.description).split(/\s+/).filter(word => word.length > 0).length}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Submit Section */}
      {errors.submit && (
        <Alert severity="error" sx={{ mt: 3 }}>
          {errors.submit}
        </Alert>
      )}

      <Box sx={{ display: 'flex', gap: 2, mt: 4, justifyContent: 'flex-end' }}>
        <Button
          variant="outlined"
          onClick={onCancel}
          disabled={uploading || formData.imageUploading}
          size="large"
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSubmit}
          disabled={uploading || formData.imageUploading}
          startIcon={uploading ? <CircularProgress size={20} /> : <CloudUploadIcon />}
          size="large"
        >
          {uploading ? 'Saving...' : (isEdit ? 'Update Handbook' : 'Create Handbook')}
        </Button>
      </Box>
    </Box>
  );
};

export default EnhancedHandbookForm;